// Phase 4: Stateless signup form component
// Extracted from Auth.tsx - signup-specific UI

import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Eye, EyeOff, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import type { SignupFormProps } from './types';

export default function SignupForm({
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  agreedToTerms,
  setAgreedToTerms,
  loading,
  passwordError,
  passwordStrength,
  passwordMatchStatus,
  onSubmit,
  onGoogleSignIn,
  onSwitchToLogin,
  onNavigateToPrivacy,
  onNavigateToTerms,
  onNavigateToDataDeletion,
}: SignupFormProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 charcoal-pro">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="text-center space-y-2">
          <Link to="/" className="text-3xl font-bold hover:text-primary transition-colors cursor-pointer">
            StudyForge
          </Link>
          <p className="text-muted-foreground">Create your account</p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          {/* Password Strength Indicator */}
          {password && (
            <div className="space-y-2">
              <div className="flex gap-1 h-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className={`flex-1 rounded-full transition-all ${
                      i <= passwordStrength.score ? passwordStrength.color : 'bg-muted'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{passwordStrength.feedback}</p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={
                  passwordMatchStatus === 'matching'
                    ? "border-green-500 focus:border-green-500"
                    : passwordMatchStatus === 'not-matching'
                    ? "border-red-500 focus:border-red-500"
                    : ""
                }
              />
              <div className="absolute right-0 top-0 h-full flex items-center gap-1 pr-2">
                {passwordMatchStatus === 'matching' && (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                )}
                {passwordMatchStatus === 'not-matching' && (
                  <XCircle className="h-4 w-4 text-red-500" />
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-full px-2"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            {passwordMatchStatus === 'matching' && (
              <p className="text-sm text-green-500 flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                Passwords match!
              </p>
            )}
            {passwordMatchStatus === 'not-matching' && (
              <p className="text-sm text-red-500 flex items-center gap-1">
                <XCircle className="h-3.5 w-3.5" />
                Passwords do not match
              </p>
            )}
          </div>

          {passwordError && (
            <p className="text-sm text-destructive">{passwordError}</p>
          )}

          {/* Terms checkbox */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="terms"
              data-testid="terms-checkbox"
              checked={agreedToTerms}
              onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
            />
            <Label htmlFor="terms" className="text-sm leading-none cursor-pointer">
              I agree to the{" "}
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToTerms();
                }}
              >
                Terms of Service
              </button>
              {" "}and{" "}
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={(e) => {
                  e.preventDefault();
                  onNavigateToPrivacy();
                }}
              >
                Privacy Policy
              </button>
            </Label>
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading...
              </>
            ) : (
              'Sign Up'
            )}
          </Button>

          {/* Divider */}
          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">or</span>
            </div>
          </div>

          {/* Google OAuth */}
          <Button
            type="button"
            variant="outline"
            className="w-full"
            disabled={loading}
            onClick={onGoogleSignIn}
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
            )}
            Continue with Google
          </Button>
        </form>

        <div className="text-center">
          <Button variant="link" onClick={onSwitchToLogin}>
            Already have an account? Sign in
          </Button>
        </div>

        <div className="text-center text-xs text-muted-foreground space-y-1">
          <div className="flex justify-center gap-3">
            <button type="button" className="hover:underline" onClick={onNavigateToPrivacy}>
              Privacy Policy
            </button>
            <span>•</span>
            <button type="button" className="hover:underline" onClick={onNavigateToTerms}>
              Terms
            </button>
            <span>•</span>
            <button type="button" className="hover:underline" onClick={onNavigateToDataDeletion}>
              Data Deletion
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
